import React from 'react'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CARD_SX, MUTED_TEXT_SX, PALETTE } from '../styles'

type ChartCardProps = {
  title: string
  description?: string
  loading: boolean
  hasError: boolean
  isEmpty: boolean
  bodyHeight?: number | 'auto'
  children: React.ReactNode
}

type ChartCardStateProps = {
  icon: string
  message: string
}

const STATE_MIN_HEIGHT = 96
const DEFAULT_BODY_HEIGHT = 192

const ChartCardState: React.FC<ChartCardStateProps> = ({ icon, message }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    gap={1}
    sx={{ height: '100%', minHeight: STATE_MIN_HEIGHT, textAlign: 'center' }}
  >
    <Box component="i" className={`fa-regular fa-${icon}`} sx={{ fontSize: 16, color: PALETTE.icon }} aria-hidden="true" />
    <Typography sx={{ fontSize: 12, color: PALETTE.muted }}>{message}</Typography>
  </Stack>
)

const ChartCardBody: React.FC<Pick<ChartCardProps, 'loading' | 'hasError' | 'isEmpty' | 'children'>> = ({
  loading,
  hasError,
  isEmpty,
  children,
}) => {
  const { t } = useTranslation()

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        animation="pulse"
        aria-busy="true"
        sx={{ width: '100%', height: '100%', minHeight: STATE_MIN_HEIGHT, borderRadius: '12px', bgcolor: PALETTE.skeleton }}
      />
    )
  }

  if (hasError) {
    return <ChartCardState icon="triangle-exclamation" message={t('stats.product_analytics.states.error')} />
  }

  if (isEmpty) {
    return <ChartCardState icon="chart-simple" message={t('stats.product_analytics.states.empty')} />
  }

  return <>{children}</>
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  description,
  loading,
  hasError,
  isEmpty,
  bodyHeight = DEFAULT_BODY_HEIGHT,
  children,
}) => (
  <Stack component="section" aria-label={title} sx={CARD_SX}>
    <Stack component="header" gap={0.5}>
      <Typography component="h3" sx={{ fontSize: 12, fontWeight: 600, color: PALETTE.text }}>{title}</Typography>
      {description && <Typography sx={MUTED_TEXT_SX}>{description}</Typography>}
    </Stack>
    <Box sx={{ height: bodyHeight }}>
      <ChartCardBody loading={loading} hasError={hasError} isEmpty={isEmpty}>
        {children}
      </ChartCardBody>
    </Box>
  </Stack>
)

export default ChartCard
